import { IBuilder } from "@/Common/Interfaces/IBuilder";
import { BilledServiceComposite } from "@/Patient/Models/Billing/BilledServiceComposite";
import { SavePrivateBillRequestModel } from "@/Patient/Models/Billing/SavePrivateBillRequestModel";
import { IBillableService } from "@/Patient/Models/UI/History/IBillableService";

class SavePrivateBillRequestBuilder implements IBuilder<SavePrivateBillRequestModel> {
    private requestData: SavePrivateBillRequestModel;

    constructor() {
        this.reset();
    }

    public reset(): this {
        this.requestData = new SavePrivateBillRequestModel();
        return this;
    }

    public setDateOfVisit(date: string): SavePrivateBillRequestBuilder {
        this.requestData.DateOfVisit = date;
        return this;
    }

    public setSiteId(siteId: string): SavePrivateBillRequestBuilder {
        this.requestData.SiteId = siteId;
        return this;
    }

    public setProviderId(providerId: string): SavePrivateBillRequestBuilder {
        this.requestData.ProviderId = providerId;
        return this;
    }

    public setCreatedById(createdById: string): SavePrivateBillRequestBuilder {
        this.requestData.CreatedById = createdById;
        return this;
    }

    public setPatientId(patientId: string): SavePrivateBillRequestBuilder {
        this.requestData.PatientId = patientId;
        return this;
    }

    public setPaymentMethod(paymentMethod: number): SavePrivateBillRequestBuilder {
        this.requestData.PaymentMethod = paymentMethod;
        return this;
    }

    public setDiscountType(discountType: number): SavePrivateBillRequestBuilder {
        this.requestData.DiscountType = discountType;
        return this;
    }

    public setGSTPercent(gstPercent: number): SavePrivateBillRequestBuilder {
        this.requestData.GSTPercent = gstPercent;
        return this;
    }

    public setQSTPercent(qstPercent: number): SavePrivateBillRequestBuilder {
        this.requestData.QSTPercent = qstPercent;
        return this;
    }

    public setHSTPercent(hstPercent: number): SavePrivateBillRequestBuilder {
        this.requestData.HSTPercent = hstPercent;
        return this;
    }

    public setGenericTaxPercent(genericTaxPercent: number): SavePrivateBillRequestBuilder {
        this.requestData.GenericTaxPercent = genericTaxPercent;
        return this;
    }

    public setDiscount(discount: number): SavePrivateBillRequestBuilder {
        this.requestData.Discount = discount;
        return this;
    }

    public setTotalGST(totalGST: number): SavePrivateBillRequestBuilder {
        this.requestData.TotalGST = totalGST;
        return this;
    }

    public setTotalQST(totalQST: number): SavePrivateBillRequestBuilder {
        this.requestData.TotalQST = totalQST;
        return this;
    }

    public setTotalHST(totalHST: number): SavePrivateBillRequestBuilder {
        this.requestData.TotalHST = totalHST;
        return this;
    }

    public setTotalGenericTax(totalGenericTax: number): SavePrivateBillRequestBuilder {
        this.requestData.TotalGenericTax = totalGenericTax;
        return this;
    }

    public setTotal(total: number): SavePrivateBillRequestBuilder {
        this.requestData.Total = total;
        return this;
    }

    public setNotes(notes: string): SavePrivateBillRequestBuilder {
        this.requestData.Notes = notes;
        return this;
    }

    public setIsFromClose(isFromClose: boolean): SavePrivateBillRequestBuilder {
        this.requestData.IsFromClose = isFromClose;
        return this;
    }

    public setIsFromEdition(isFromEdition: boolean): SavePrivateBillRequestBuilder {
        this.requestData.IsFromEdition = isFromEdition;
        return this;
    }

    public setPrivateBillId(privateBillId: string): SavePrivateBillRequestBuilder {
        this.requestData.PrivateBillId = privateBillId;
        return this;
    }

    public setRecipientName(recipientName: string): SavePrivateBillRequestBuilder {
        this.requestData.RecipientName = recipientName;
        return this;
    }

    public setRecipientAddress(recipientAddress: string): SavePrivateBillRequestBuilder {
        this.requestData.RecipientAddress = recipientAddress;
        return this;
    }

    public setRecipientPhoneNumber(recipientPhoneNumber: string): SavePrivateBillRequestBuilder {
        this.requestData.RecipientPhoneNumber = recipientPhoneNumber;
        return this;
    }

    public setRecipientFaxNumber(recipientFaxNumber: string): SavePrivateBillRequestBuilder {
        this.requestData.RecipientFaxNumber = recipientFaxNumber;
        return this;
    }

    public setBilledTo(billedTo: number): SavePrivateBillRequestBuilder {
        this.requestData.BilledTo = billedTo;
        return this;
    }

    public setEncounterId(encounterId: string): SavePrivateBillRequestBuilder {
        this.requestData.EncounterId = encounterId;
        return this;
    }

    public setBilledServices(billedServices: IBillableService[]): SavePrivateBillRequestBuilder {
        for (let service of billedServices) {
            if (service.get("SelectedService") == null)
                continue;

            const billData = new BilledServiceComposite({
                Qty: service.get("Qty"),
                Price: service.get("Price"),
                ServiceNumber: service.get("ServiceNumber"),
                NameFr: service.get("NameFr"),
                NameEn: service.get("NameEn"),
                NameEs: service.get("NameEs"),
                GST: service.get("GST"),
                QST: service.get("QST"),
                HST: service.get("HST"),
                GenericTax: service.get("GenericTax"),
                HadGST: service.get("HasGST") == true ? true : false,
                HadQST: service.get("HasQST") == true ? true : false,
                HadHST: service.get("HasHST") == true ? true : false,
                HadGenericTax: service.get("HasGenericTax") == true ? true : false,
                CostCenterId: service.get("SelectedService").Service.CostCenterId
            });

            this.requestData.BilledServices.push(billData);
        }

        return this;
    }

    public build(): SavePrivateBillRequestModel {
        return this.requestData;
    }
}

export { SavePrivateBillRequestBuilder };
